/**
 * DocumentUpload Component - Enhanced upload UI with drag and drop
 * Includes client-side validation with server-side limits
 */

import React, { useState, useRef } from 'react'
import { Card } from '../ui'
import { Upload, X, File, CheckCircle, AlertCircle } from 'lucide-react'

// ✅ Constants - Match server-side limits
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_FILE_SIZE_MB = 10

const ALLOWED_TYPES = {
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'image/jpeg': '.jpg,.jpeg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'text/plain': '.txt',
  'text/csv': '.csv',
}

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.txt', '.csv']

const DocumentUpload = ({
  onUpload,
  onCancel,
  acceptedTypes = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv',
  maxSize = MAX_FILE_SIZE,
  multiple = false,
  className = '',
  label = 'Upload Document',
  description = 'Drop your document here or click to browse',
  maxFiles = 5,
  ...props
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef(null)

  // ✅ Enhanced validation
  const validateFile = (file) => {
    // Check file size (client-side)
    if (file.size > maxSize) {
      return { 
        valid: false, 
        error: `File size exceeds ${maxSize / 1024 / 1024}MB limit` 
      }
    }

    // Check file extension
    const fileExt = '.' + file.name.split('.').pop().toLowerCase()
    const types = acceptedTypes.split(',').map(t => t.trim())
    
    const isValid = types.some(t => {
      if (t.startsWith('.')) return fileExt === t
      return file.type === t
    })

    if (!isValid) {
      return { 
        valid: false, 
        error: `File type not supported. Accepted: ${acceptedTypes}` 
      }
    }

    // Check for empty file
    if (file.size === 0) {
      return { 
        valid: false, 
        error: 'File is empty' 
      }
    }

    // ✅ Check for malicious file names
    const dangerousPatterns = ['..', '/', '\\', '%00', ';', '|', '&', '$', '(', ')']
    for (const pattern of dangerousPatterns) {
      if (file.name.includes(pattern)) {
        return {
          valid: false,
          error: 'File name contains invalid characters'
        }
      }
    }

    return { valid: true }
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    
    // Check max files
    if (droppedFiles.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`)
      setTimeout(() => setError(null), 5000)
      return
    }

    const validFiles = []
    const errors = []

    droppedFiles.forEach((file) => {
      const result = validateFile(file)
      if (result.valid) {
        validFiles.push(file)
      } else {
        errors.push(`${file.name}: ${result.error}`)
      }
    })

    if (errors.length > 0) {
      setError(errors.join('\n'))
      setTimeout(() => setError(null), 8000)
    }

    if (validFiles.length > 0) {
      setFiles(prev => multiple ? [...prev, ...validFiles] : validFiles)
    }
  }

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files)
    
    // Check max files
    if (selectedFiles.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`)
      setTimeout(() => setError(null), 5000)
      e.target.value = ''
      return
    }

    const validFiles = []
    const errors = []

    selectedFiles.forEach((file) => {
      const result = validateFile(file)
      if (result.valid) {
        validFiles.push(file)
      } else {
        errors.push(`${file.name}: ${result.error}`)
      }
    })

    if (errors.length > 0) {
      setError(errors.join('\n'))
      setTimeout(() => setError(null), 8000)
    }

    if (validFiles.length > 0) {
      setFiles(prev => multiple ? [...prev, ...validFiles] : validFiles)
    }
    
    // Reset input
    e.target.value = ''
  }

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (files.length === 0) return

    setUploading(true)
    setUploadProgress(0)
    setError(null)

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval)
            return prev
          }
          return prev + 5
        })
      }, 100)

      await onUpload?.(files)
      
      clearInterval(progressInterval)
      setUploadProgress(100)
      
      // Success - clear files after short delay
      setTimeout(() => {
        setFiles([])
        setUploadProgress(0)
      }, 1000)
      
    } catch (err) {
      setError(err.message || 'Upload failed')
      setTimeout(() => setError(null), 5000)
    } finally {
      setUploading(false)
    }
  }

  const handleCancel = () => {
    setFiles([])
    setError(null)
    setUploadProgress(0)
    onCancel?.()
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  return (
    <Card className={`p-6 ${className}`} {...props}>
      {/* Drop zone */}
      <div
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200
          ${isDragging ? 'border-brand bg-brand-soft' : 'border-neutral-200 hover:border-neutral-300'}
          ${error ? 'border-error bg-error-soft' : ''}
          ${uploading ? 'opacity-60 pointer-events-none' : ''}
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          multiple={multiple}
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />

        <div className="pointer-events-none">
          <div className="w-12 h-12 bg-brand-soft rounded-full flex items-center justify-center mx-auto mb-3">
            {uploading ? (
              <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            ) : (
              <Upload size={20} className="text-brand" />
            )}
          </div>
          <p className="text-sm font-semibold text-ink">
            {uploading ? 'Uploading...' : label}
          </p>
          <p className="text-xs text-muted mt-1">
            {uploading ? 'Please wait...' : description}
          </p>
          <p className="text-[10px] text-muted mt-2">
            {acceptedTypes.replace(/\./g, '').toUpperCase()} · Max {maxSize / 1024 / 1024}MB · Max {maxFiles} files
          </p>
        </div>

        {/* Upload progress bar */}
        {uploading && (
          <div className="absolute bottom-4 left-4 right-4">
            <div className="w-full h-1.5 bg-neutral-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-brand transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mt-3 p-3 bg-error-soft border border-error rounded-lg flex items-start gap-2 animate-fade-in">
          <AlertCircle size={16} className="text-error shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-error whitespace-pre-wrap">{error}</p>
          </div>
          <button 
            onClick={() => setError(null)}
            className="text-error/60 hover:text-error transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">
            {files.length} file{files.length > 1 ? 's' : ''} selected
          </p>
          {files.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
              <div className="flex items-center gap-3 min-w-0">
                <File size={16} className="text-neutral-400 shrink-0" />
                <span className="text-sm text-ink truncate">{file.name}</span>
                <span className="text-xs text-muted shrink-0">
                  {formatFileSize(file.size)}
                </span>
                {file.type && (
                  <span className="text-[10px] text-muted bg-neutral-100 px-1.5 py-0.5 rounded">
                    {file.type.split('/')[1] || file.type}
                  </span>
                )}
              </div>
              <button
                onClick={() => removeFile(index)}
                className="text-neutral-400 hover:text-error transition-colors p-1"
                disabled={uploading}
                aria-label="Remove file"
              >
                <X size={16} />
              </button>
            </div>
          ))}

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleCancel}
              className="btn-secondary text-sm flex-1"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading || files.length === 0}
              className="btn-primary text-sm flex-1 flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload {files.length} file{files.length > 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Card>
  )
}

DocumentUpload.displayName = 'DocumentUpload'

export default DocumentUpload